/**
 * **出版社存哪个值** —— 全项目**唯一一份**口径（前端、服务层、读侧筛选都引这里）。
 *
 * ## 为什么必须有这一份（2026-10-18 生产 bug）
 *
 * 用户实测：「出版社」下拉里有**三项**：`全部出版社`、`pub_be07fb11`、
 * `人民教育出版社`；选「人民教育出版社」+ 小学一年级 + 上册 + 识字表 → **0 条**，
 * 选那串 `pub_…` 却有 280 条。
 *
 * 根因不是数据"脏"，而是**两条建书路径写库的口径不一致**：
 *
 * | 路径 | 代码位置 | 旧 `textbook.publisher` |
 * | --- | --- | --- |
 * | 「＋ 添加出版社」弹窗 | `POST /admin/dictation/textbooks` | `pub_` + 人话的 FNV-1a 哈希（`pub_be07fb11`） |
 * | 「按书录入」保存时自动建书 | `importDictation` → `resolveTextbook()` | 人话（`人民教育出版社`） |
 *
 * 而唯一键是 `(publisher, subject, grade_code, semester)`（迁移 0015），
 * 所以同一个出版社的两个值**各自能存在** —— 下拉里就是两项，各查到一半的字。
 *
 * ## 定稿：库里存**人话**（`人民教育出版社`）
 *
 * 理由（按分量排）：
 *   1. **存量数据本来就有**：生产 114 个字挂在 `人民教育出版社` 那本下册下面，
 *      而 `pub_be07fb11` 那本的上册只是"代号"这一种历史写法；
 *   2. **界面要显示的就是它**：下拉、列表、提示语里写的都是人话，
 *      存人话就不需要任何"代号 → 人话"的反查（哈希**不可逆**，反查只能靠 `name`，
 *      而 `name` 是用户可以随手改的展示名）；
 *   3. **代号是历史包袱**：`pub_` + 32 位哈希有两个已知代价（哈希碰撞、
 *      用户看不出这是哪家），而它换来的"后端只收小写英文"这一条约束已经没有意义
 *      —— zod 那一层现在直接收人话。
 *
 * ## 归一（`canonicalPublisherName`）只做无歧义的改动
 *
 *   · 复用 `normalizeTextbookText()`（全角数字 → 半角、空白折叠、汉字间空格删掉）；
 *   · 纯 ASCII 的代号形状（`RenJiao` / `ren jiao`）→ 小写去空白（与
 *     `textbookRefKey()` 同口径，避免 `RenJiao` 与 `renjiao` 变成两本）；
 *   · ⛔ **不猜**：`pub_xxxxxxxx` 是**不可逆**的哈希，猜不出原名，所以
 *     `canonicalPublisherName()` 对它返回**空串**，由调用方给出 400
 *     （"请写出版社名称，不要用 pub_ 代号"）—— 宁可报错，也不把代号再写进库一次。
 *
 * ## 展示（`publisherDisplayNameOf`）
 *
 * 历史行的形状是 `publisher = pub_xxxxxxxx` / `name = 人民教育出版社`，
 * 所以显示时：`publisher` 不是代号就用它，是代号就退回 `name`。
 * 下拉、筛选、列表**都引这一个函数**，界面上不该再出现第二套判断。
 */
import { normalizeTextbookText, textbookRefKey } from './textbook-identity.js';

/** 由人话算出来的历史代号带这个前缀（只用来**认出来**，不再写库）。 */
export const PUBLISHER_CODE_PREFIX = 'pub_';

/** 出版社名的最大长度（与 `textbook.publisher` 的 `varchar(32)` 对齐）。 */
export const PUBLISHER_NAME_MAX = 32;

/**
 * 用户自己编的出版社代号（`renjiao` / `bubian`）—— 历史上允许，仍然原样保留。
 *
 * ⚠️ 只用来判"这串是不是纯 ASCII 代号形状"，不再用来限定"只能填代号"。
 */
const ASCII_CODE_RE = /^[a-z][a-z0-9_]*$/;

/** 历史代号：`pub_` + 8 位十六进制（`publisherCodeOf()` 的产物）。 */
const GENERATED_CODE_RE = new RegExp(`^${PUBLISHER_CODE_PREFIX}[0-9a-f]{8}$`);

/**
 * 这串是不是"由人话算出来的历史代号"（`pub_be07fb11`）。
 *
 * 判据只有形状：它不是一家出版社的名字，界面上**永远不该显示它**。
 */
export function isGeneratedPublisherCode(value: string | null | undefined): boolean {
  return GENERATED_CODE_RE.test((value ?? '').trim());
}

/**
 * 出版社 → **写库用的标准值**（人话）。
 *
 * 例：
 *   `'人民教育出版社'`      → `'人民教育出版社'`
 *   `' 人民教育　出版社 '`   → `'人民教育出版社'`（空白归一，见 `normalizeTextbookText`）
 *   `'RenJiao'`             → `'renjiao'`（ASCII 代号形状：小写去空白）
 *   `'ren jiao'`            → `'renjiao'`
 *   `''` / `'pub_be07fb11'` → `''`（调用方必须报错：代号不可逆、不能写库）
 *
 * ⚠️ 空串返回 `''` 时**不要**悄悄换成别的值：那是"我不知道这家叫什么"，
 *    两种调用方（建书 / 导入自动建书）都必须把它变成一句人话的错误。
 * ⚠️ 用空串兜底（例如用 `name` 顶替）是**不允许**的：`name` 是展示名
 *    （可以是"人教版（部编）"），拿它当出版社会把一家出版社拆成两个身份 ——
 *    那正是这次要修的病。
 */
export function canonicalPublisherName(raw: string | null | undefined): string {
  const text = normalizeTextbookText(raw ?? '');
  if (text === '' || isGeneratedPublisherCode(text)) return '';
  /** ASCII 代号形状：小写、去空白（与 `textbookRefKey()` 同口径）。 */
  const compact = text.replace(/\s+/g, '').toLowerCase();
  if (ASCII_CODE_RE.test(compact)) return compact;
  return text;
}

/**
 * 一本书在界面上**显示**的出版社名（`publisherDisplayName` 的唯一实现）。
 *
 *   · `publisher` 是人话 → 用它（新数据、导入建的书都是这样）；
 *   · `publisher` 是历史代号（`pub_xxxxxxxx`）→ 退回 `name`（那才是人话）；
 *   · 都空 → 空串（调用方决定怎么显示"没有出版社"）。
 */
export function publisherDisplayNameOf(
  textbook: { publisher?: string | null; name?: string | null } | null | undefined,
): string {
  const publisher = normalizeTextbookText(textbook?.publisher ?? '');
  if (publisher !== '' && !isGeneratedPublisherCode(publisher)) return publisher;
  return normalizeTextbookText(textbook?.name ?? '');
}

/**
 * 一家出版社的**比较键**（空白 / 大小写不算区别，与库里的匹配口径同一套）。
 *
 * ⚠️ 用**显示名**算：历史行（`publisher = pub_…`）与导入行（`publisher = 人话`）
 *    必须落到同一个键上 —— 否则下拉里还是两项（这正是本次的病灶）。
 */
export function publisherRefKeyOf(
  textbook: { publisher?: string | null; name?: string | null } | null | undefined,
): string {
  return textbookRefKey(publisherDisplayNameOf(textbook));
}
